import React, { useState } from 'react';
import { Smartphone, Check, ArrowRight, AlertTriangle } from 'lucide-react';
import { getBrandName } from '../utils/brand';
import { detectInAppBrowser } from '../utils/inappBrowser';
import { SHOW_INAPP_GUIDE_EVENT } from './InAppBrowserNotice';

interface PWAInstallerProps {
  onInstallComplete: () => void;
  installPrompt: any;
}

export function PWAInstaller({ onInstallComplete, installPrompt }: PWAInstallerProps) {
  const [installed, setInstalled] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);

  // [2026-09-12]
  // 카카오톡으로 초대 링크를 받은 사람은 대부분 카톡 인앱 브라우저로 들어온다.
  // 인앱 브라우저는 PWA 설치를 지원하지 않아서 여기서 설치를 시도해도 100% 실패한다.
  // 설치 방법은 중앙 팝업(InAppBrowserNotice)이 자동으로 띄워주고,
  // 여기서는 팝업을 닫은 사람이 다시 열 수 있는 버튼만 둔다.
  const inAppName = detectInAppBrowser();
  const showGuide = () => window.dispatchEvent(new Event(SHOW_INAPP_GUIDE_EVENT));

  // 브라우저가 실제로 설치 가능하다고 판단했을 때만(beforeinstallprompt 캡처됨)
  // 네이티브 설치창을 띄운다. iOS Safari 등 미지원 브라우저는 installPrompt가
  // 항상 null이므로 안내 문구 + "계속하기"만 보여주고 앱 사용을 막지 않는다.
  const handleInstall = async () => {
    // 인앱 브라우저에서는 설치 프롬프트를 띄우면 안 된다(실패만 한다). 그냥 통과.
    if (!installPrompt || inAppName) {
      onInstallComplete();
      return;
    }
    setIsPrompting(true);
    try {
      installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setInstalled(true);
        setTimeout(onInstallComplete, 1200);
      } else {
        onInstallComplete();
      }
    } catch {
      onInstallComplete();
    } finally {
      setIsPrompting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: "linear-gradient(135deg, #FF6B00 0%, #FF8C00 50%, #FFB347 100%)"}}>
      <div className="text-center max-w-sm w-full">
        <div className="relative mb-12">
          <div className="w-28 h-28 bg-white rounded-3xl flex items-center justify-center mx-auto shadow-2xl">
            <img src="/handy-logo.svg" alt="Handy" width="80" height="80" />
          </div>
        </div>

        <h1 className="text-5xl font-bold text-white mb-3 tracking-tight drop-shadow">{getBrandName()}</h1>
        <p className="text-orange-100 text-xl mb-12 font-medium">Next-Gen Digital Wallet</p>

        {installed ? (
          <div className="flex items-center justify-center space-x-2 text-green-300">
            <Check className="w-6 h-6" />
            <span className="text-lg font-medium">홈 화면에 추가되었습니다!</span>
          </div>
        ) : (
          <div className="space-y-6">
            {inAppName && (
              <button
                onClick={showGuide}
                className="w-full rounded-2xl px-4 py-3.5 flex items-center gap-3 text-left"
                style={{ background: '#FFFFFF', boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}
              >
                <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: '#C62828' }} />
                <span className="flex-1">
                  <span className="block text-sm font-bold" style={{ color: '#C62828' }}>
                    {inAppName}에서는 설치가 안 됩니다
                  </span>
                  <span className="block text-xs mt-0.5" style={{ color: '#7F1D1D' }}>
                    설치 방법 보기 (3단계)
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 shrink-0" style={{ color: '#C62828' }} />
              </button>
            )}

            <button
              onClick={handleInstall}
              disabled={isPrompting}
              className="w-full bg-white text-slate-900 hover:bg-gray-100 font-bold text-lg py-5 rounded-2xl shadow-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70"
            >
              <Smartphone className="w-6 h-6" />
              {installPrompt && !inAppName ? '홈 화면에 설치' : '일단 둘러보기'}
              {(!installPrompt || inAppName) && <ArrowRight className="w-5 h-5" />}
            </button>

            {!installPrompt && !inAppName && (
              <p className="text-orange-100 text-xs">
                브라우저 메뉴의 "홈 화면에 추가"로 앱처럼 설치할 수도 있습니다.
              </p>
            )}

            <div className="text-center space-y-2">
              <p className="text-orange-100 text-sm">📱 Access like a native app</p>
              <p className="text-orange-100 text-sm">⚡ Instant loading</p>
              <p className="text-orange-100 text-sm">🔒 Secure & private</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
